import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import ErrorBoundary from "../../components/ErrorBoundary.jsx";
import FullScreenPanel from "./FullScreenPanel.jsx";

// Phone screens never call window.alert / confirm / prompt (plan rule 3): these show the same
// question as a full-screen panel and resolve like the native call would.
//   await phoneAlert("Saved.")                              -> undefined
//   await phoneConfirm("Delete it?", {confirmLabel, danger}) -> true | false
//   await phonePrompt("Reason?", "Damaged part", {inputMode})  -> string | null (Cancel)
//   await phoneAsk("Email them?", {yesLabel, noLabel})        -> true | false | null (closed)
// Imperative like utils/pinPrompt.js, so shared action modules can call them from anywhere.

function mount(render) {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);
    let done = false;
    const finish = (value) => {
      if (done) return;
      done = true;
      root.unmount();
      container.remove();
      resolve(value);
    };
    root.render(
      <ErrorBoundary
        fallback={() => (
          <div className="alert alert-danger m-3" role="alert" style={{ position: "fixed", zIndex: 2101, top: 0, left: 0, right: 0 }}>
            This message could not be shown.
            <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={() => finish(undefined)}>Close</button>
          </div>
        )}
      >
        {render(finish)}
      </ErrorBoundary>
    );
  });
}

export function phoneAlert(message, { title = "JobQ", okLabel = "OK" } = {}) {
  return mount((finish) => (
    <FullScreenPanel
      open
      title={title}
      onClose={() => finish(undefined)}
      testId="phone-alert"
      footer={
        <button type="button" className="btn btn-primary btn-lg w-100" onClick={() => finish(undefined)}>
          {okLabel}
        </button>
      }
    >
      <p className="jq-dialog-text">{message}</p>
    </FullScreenPanel>
  ));
}

export function phoneConfirm(message, { title = "Are you sure?", confirmLabel = "Yes", cancelLabel = "Cancel", danger = false } = {}) {
  return mount((finish) => (
    <FullScreenPanel
      open
      title={title}
      onClose={() => finish(false)}
      testId="phone-confirm"
      footer={
        <div className="jq-two-buttons">
          <button type="button" className="btn btn-outline-secondary btn-lg" onClick={() => finish(false)}>
            {cancelLabel}
          </button>
          <button type="button" className={`btn btn-lg ${danger ? "btn-danger" : "btn-primary"}`} onClick={() => finish(true)}>
            {confirmLabel}
          </button>
        </div>
      }
    >
      <p className="jq-dialog-text">{message}</p>
    </FullScreenPanel>
  )).then((v) => v === true);
}

/** A yes / no question where backing out is a third answer: true (yesLabel), false (noLabel), or
 *  null when the panel is closed (X, Escape). For a choice that must not be made by closing, e.g.
 *  "email the customer?" after the PIN for a shipment: closing stops the shipment. */
export function phoneAsk(message, { title = "JobQ", yesLabel = "Yes", noLabel = "No" } = {}) {
  return mount((finish) => (
    <FullScreenPanel
      open
      title={title}
      onClose={() => finish(null)}
      closeLabel="Cancel"
      testId="phone-ask"
      footer={
        <div className="jq-two-buttons">
          <button type="button" className="btn btn-outline-primary btn-lg" onClick={() => finish(false)}>
            {noLabel}
          </button>
          <button type="button" className="btn btn-primary btn-lg" onClick={() => finish(true)}>
            {yesLabel}
          </button>
        </div>
      }
    >
      <p className="jq-dialog-text">{message}</p>
    </FullScreenPanel>
  )).then((v) => (v === true || v === false ? v : null));
}

let promptSeq = 0;

export function phonePrompt(message, defaultValue = "", { title = "JobQ", okLabel = "OK", cancelLabel = "Cancel", inputMode } = {}) {
  return mount((finish) => (
    <PromptPanel
      message={message}
      defaultValue={defaultValue == null ? "" : String(defaultValue)}
      title={title}
      okLabel={okLabel}
      cancelLabel={cancelLabel}
      inputMode={inputMode}
      onDone={finish}
    />
  )).then((v) => (typeof v === "string" ? v : null));
}

function PromptPanel({ message, defaultValue, title, okLabel, cancelLabel, inputMode, onDone }) {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);
  const [id] = useState(() => `jq-prompt-${++promptSeq}`);
  useEffect(() => {
    // After FullScreenPanel focuses its close button (also a 0 ms timer, scheduled first).
    const t = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 0);
    return () => clearTimeout(t);
  }, []);
  return (
    <FullScreenPanel
      open
      title={title}
      onClose={() => onDone(null)}
      testId="phone-prompt"
      footer={
        <div className="jq-two-buttons">
          <button type="button" className="btn btn-outline-secondary btn-lg" onClick={() => onDone(null)}>
            {cancelLabel}
          </button>
          <button type="submit" form={id} className="btn btn-primary btn-lg">
            {okLabel}
          </button>
        </div>
      }
    >
      <form id={id} onSubmit={(e) => { e.preventDefault(); onDone(value); }}>
        <label htmlFor={`${id}-input`} className="jq-dialog-text d-block mb-3">{message}</label>
        <input
          id={`${id}-input`}
          ref={inputRef}
          className="form-control form-control-lg"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          inputMode={inputMode}
          autoComplete="off"
        />
      </form>
    </FullScreenPanel>
  );
}
